import { useEffect, useState } from "react";
import axios from "axios";
import confetti from "canvas-confetti";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, BadgeCheck, Heart, Home } from "lucide-react";
import { Seo } from "@/components/Seo";
import { API } from "@/lib/api";

const COLORS = ["#EF4444", "#3B82F6", "#22C55E", "#F59E0B", "#A855F7"];

export default function ThankYou() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/orders/${orderId}`)
      .then((r) => {
        setOrder(r.data);
        if (r.data.download_ready) {
          confetti({ particleCount: 120, angle: 60, spread: 70, origin: { x: 0 }, colors: COLORS });
          confetti({ particleCount: 120, angle: 120, spread: 70, origin: { x: 1 }, colors: COLORS });
        }
      })
      .catch(() => setError(true));
  }, [orderId]);

  if (error) {
    return (
      <div className="mx-auto max-w-md px-6 py-20 text-center">
        <div data-testid="order-error" className="rounded-[2rem] bg-white p-10 shadow-xl shadow-blue-900/5">
          <h1 className="font-display text-3xl font-bold text-slate-800">Order not found</h1>
          <p className="mt-3 font-semibold text-slate-500">
            We couldn't find this order. Please check your link or place a new order.
          </p>
          <Link to="/" data-testid="back-home-button"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue px-8 py-3 font-display text-lg font-bold text-white shadow-[0_5px_0_0_#1D4ED8] active:translate-y-1 active:shadow-none">
            <Home className="h-5 w-5" /> Back to Store
          </Link>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div data-testid="thank-you-page" className="flex items-center justify-center px-6 py-16">
      <Seo title="Order Confirmed | Divine Kids Learning" description="Thank you for your purchase. Download your printable PDF workbook." />
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 70, damping: 14 }}
        className="w-full max-w-xl rounded-[2.5rem] bg-white p-10 text-center shadow-xl shadow-blue-900/5 sm:p-12"
      >
        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
          <BadgeCheck className="h-10 w-10 text-brand-green" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-black tracking-tight text-slate-800 sm:text-4xl">
          Thank you for supporting Divine Kids Learning!
        </h1>
        <p className="mt-4 text-lg font-semibold text-slate-500">
          Your order for <span className="font-bold text-slate-700">{order.product_name}</span> is
          confirmed. Happy learning!
        </p>
        <p className="mt-6 flex items-center justify-center gap-2 font-display text-lg font-bold text-slate-600">
          Small Steps <Heart className="h-5 w-5 fill-brand-red text-brand-red" /> Big Dreams
        </p>

        {order.download_ready ? (
          <a
            data-testid="download-pdf-button"
            href={`${API}/download/${order.order_id}`}
            className="mt-8 inline-flex h-16 w-full items-center justify-center gap-3 rounded-full bg-brand-green font-display text-2xl font-bold text-white shadow-[0_6px_0_0_#15803D] transition-transform active:translate-y-1 active:shadow-none"
          >
            <Download className="h-6 w-6" /> Download Your Workbook
          </a>
        ) : (
          <p data-testid="payment-pending-note"
            className="mt-8 rounded-2xl bg-amber-50 px-6 py-4 font-semibold text-amber-700">
            Your payment reference is being verified. Your download will unlock here automatically,
            and we'll email you the link the moment it's confirmed.
          </p>
        )}

        <Link to="/shop" data-testid="back-to-store-link"
          className="mt-6 inline-block font-display text-base font-bold text-brand-blue underline-offset-4 hover:underline">
          Continue shopping
        </Link>
      </motion.div>
    </div>
  );
}
